import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { touchHitArea } from '@/components/ui/touch-target';
import WaveSurfer from 'wavesurfer.js';

// The player's controls stay compact, so on phones and touch tablets they get
// an invisible 44px hit area (the play button also grows on phones, and
// touch:gap-3 keeps the two rows' hit areas apart). Mute's hit area reaches
// 14px past its icon, so on those screens the bottom row keeps 16px clear of
// footerAction and wraps it onto its own line when the row is too narrow.
const HIT_AREA = touchHitArea('', { coarse: true });

interface CallRecordingPlayerProps {
	recordingUrl: string;
	callDuration?: number;
	variant?: 'default' | 'voicemail';
	footerAction?: React.ReactNode;
}

const CallRecordingPlayer: React.FC<CallRecordingPlayerProps> = ({
	recordingUrl,
	variant = 'default',
	footerAction,
}) => {
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [playbackRate, setPlaybackRate] = useState(1);
	const [isMuted, setIsMuted] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const waveSurferRef = useRef<WaveSurfer | null>(null);

	// Format audio player time: 154 -> "2:34"
	const formatAudioTime = (seconds: number): string => {
		if (!seconds || isNaN(seconds) || !isFinite(seconds)) {
			return '0:00';
		}
		const mins = Math.floor(seconds / 60);
		const secs = Math.floor(seconds % 60);
		return `${mins}:${secs.toString().padStart(2, '0')}`;
	};

	useEffect(() => {
		if (!containerRef.current) return;

		const wavesurfer = WaveSurfer.create({
			container: containerRef.current,
			waveColor: '#d1d5db', // gray-300
			progressColor: '#3b82f6', // blue-500
			cursorColor: 'transparent',
			barWidth: 3,
			barRadius: 3,
			barGap: 2,
			height: 32,
			normalize: true,
			url: recordingUrl,
		});

		waveSurferRef.current = wavesurfer;

		wavesurfer.on('ready', () => {
			setDuration(wavesurfer.getDuration());
		});

		wavesurfer.on('audioprocess', (time) => {
			setCurrentTime(time);
		});

		wavesurfer.on('interaction', (time) => {
			setCurrentTime(time);
		});

		wavesurfer.on('finish', () => {
			setIsPlaying(false);
			setCurrentTime(0);
		});

		wavesurfer.on('play', () => setIsPlaying(true));
		wavesurfer.on('pause', () => setIsPlaying(false));

		return () => {
			wavesurfer.destroy();
		};
	}, [recordingUrl]);

	const togglePlayPause = () => {
		if (waveSurferRef.current) {
			waveSurferRef.current.playPause();
		}
	};

	const togglePlaybackSpeed = () => {
		const speeds = [1, 1.5, 2];
		const currentIndex = speeds.indexOf(playbackRate);
		const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
		setPlaybackRate(nextSpeed);
		if (waveSurferRef.current) {
			waveSurferRef.current.setPlaybackRate(nextSpeed);
		}
	};

	const toggleMute = () => {
		if (waveSurferRef.current) {
			const newMutedState = !isMuted;
			waveSurferRef.current.setMuted(newMutedState);
			setIsMuted(newMutedState);
		}
	};

	return (
		<div className="w-full flex flex-col gap-2 touch:gap-3 p-2 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800">
			{/* Top Row: Controls & Waveform */}
			<div className="flex items-center gap-3 px-2 pt-1">
				<Button
					onClick={togglePlayPause}
					variant="ghost"
					size="icon"
					aria-label={isPlaying ? 'Pause recording' : 'Play recording'}
					className={cn(
						'flex-shrink-0 w-8 h-8 rounded-full p-0 transition-colors duration-200 shadow-sm max-md:size-11 max-md:[&_svg]:size-5',
						HIT_AREA,
						isPlaying
							? 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700'
							: 'bg-white text-blue-600 border border-gray-200 hover:bg-white hover:border-blue-300 dark:bg-slate-800 dark:text-blue-400 dark:border-slate-700 dark:hover:bg-slate-800 dark:hover:border-blue-400'
					)}
				>
					{isPlaying ? (
						<Pause className="w-3.5 h-3.5 fill-current" />
					) : (
						<Play className="w-3.5 h-3.5 fill-current ml-0.5" />
					)}
				</Button>

				{/* Waveform */}
				<div className="flex-grow h-8 relative min-w-[100px]">
					<div ref={containerRef} className="w-full h-full" />
				</div>

				<span className="text-xs text-gray-500 dark:text-slate-400 font-mono font-medium tabular-nums w-[32px] text-right">
					{formatAudioTime(currentTime)}
				</span>
			</div>

			{/* Divider */}
			<div className="h-px w-full bg-slate-200/60 dark:bg-slate-700/60"></div>

			{/* Bottom Row: Actions */}
			<div className="flex justify-between items-center px-2 pb-1 max-md:flex-wrap max-md:gap-4 touch:flex-wrap touch:gap-4">
				<div className="flex items-center gap-2 max-md:gap-4 touch:gap-4">
					<Button
						onClick={togglePlaybackSpeed}
						variant="ghost"
						size="sm"
						className={cn(
							'h-auto p-0 text-[11px] font-bold text-gray-400 hover:text-blue-600 dark:text-slate-500 dark:hover:text-blue-400 tracking-wider uppercase transition-colors',
							HIT_AREA
						)}
					>
						{playbackRate}x Speed
					</Button>
					<Button
						onClick={toggleMute}
						variant="ghost"
						size="icon"
						className={cn(
							'h-auto w-auto p-0 text-gray-400 hover:text-blue-600 dark:text-slate-500 dark:hover:text-blue-400 transition-colors',
							HIT_AREA
						)}
						aria-label={isMuted ? 'Unmute' : 'Mute'}
					>
						{isMuted ? (
							<VolumeX className="w-3.5 h-3.5" />
						) : (
							<Volume2 className="w-3.5 h-3.5" />
						)}
					</Button>
				</div>

				{footerAction && <div className="max-md:ml-auto touch:ml-auto">{footerAction}</div>}
			</div>
		</div>
	);
};

export default CallRecordingPlayer;
